import { ElNotification, ElMessageBox } from "element-plus";
import { fa } from "element-plus/es/locale";
import { CallHistory, Customer, User } from "~/types";
import { InCall } from "~/types/enums/InCall";
import {
  getRequest,
  postRequest,
  putRequest,
} from "~/apis/requests/useApiCall";
import { MessageOrigin } from "~/types/enums/messageOrigin";
import { CallOrigin } from "~/types/enums/callOrigin";
import { Profile } from "~/types/profile";

const gsm7 =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\u0020!\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const gsm7Extended = "^{}\\[~]|€";

const gsm7Set = new Set([...gsm7]);
const gsm7ExtSet = new Set([...gsm7Extended]);
interface SmsCounterResult {
  remaining: number;
  segments: number;
}

export const useDialogStore = definePiniaStore("dialogStore", {
  state: () => ({
    _showIncomingCallDialog: false,
    _showMaximizeButton: false,
    _showOngoingCallDialog: false,
    _showShortcutList: false,
    showCallWarning: false,
    showDialer: false,
    _showMoreModal: false,
    _showAddCustomer: false,
    _ongoingCallNumber: "",
    _ongoingCallOrigin: CallOrigin.CC as CallOrigin | null,
    _showTeleSalesEditDialog: false,
    _showAddNotes: false,
    showAddTasks: false,
    _isSmsPopUpVisible: false,
    _isBulkSmsPopUpVisible: false,
    _smsNumber: "",
    _inCall: InCall.IDLE as InCall,
    _isIncoming: false,
    _currentCustomer: null as Customer | null,
    _currentProfile: null as Profile | null,
    _currentCallHistoryId: null as number | null,
    _transferableAgents: [] as User[],
    _ƒsethowMoreModal: false,
    _showTransferCallModal: false,
    _phoneNoToSet: "",
    _callDuration: 0, // totalSeconds moved here
    _teleSaleInfo: {} as { teleSale: any; number: string },
    _lastCallAgent: null as Pick<User, "id" | "name" | "user_name"> | null,
  }),
  actions: {
    async getTransferableAgents() {
      const { response, error } = await getRequest<User[]>(
        "cc/call-transfer/available-agents",
      );
      if (!error) {
        this.$state._transferableAgents = response.data;
      }
    },
    async sendSms(requestData: { text: string; origin?: MessageOrigin }) {
      const { error } = await postRequest<any>("/sms/send", {
        number: this._smsNumber,
        ...requestData,
      });
      if (!error) {
        ElNotification({
          type: "success",
          title: "SMS Sent",
        });
        return true;
      }

      const validationError = error?.response?.data?.errors?.number?.[0];

      ElMessageBox({
        title: "Error Sending SMS",
        type: "error",
        message:
          validationError || error.response?.data?.message || error.message,
      });

      return false;
    },
    async addNote(formData: { remarks: string }) {
      const { error } = await putRequest<CallHistory>(
        `cc/agent-call-histories/${this._currentCallHistoryId}`,
        formData,
      );
      if (!error) {
        ElNotification({
          type: "success",
          title: "Sent",
          message: "Note added successfully",
        });
        this.toggleAddShowNotesDialogModal();
      }
    },
    async initiateCall(
      number: string,
      options: object = {},
      teleSalesId?: number,
    ) {
      return await postRequest<CallHistory>("cc/agent/initiate-call", {
        number,
        ...options,
        teleSalesId,
      });
    },

    // smsCounterFunction
    smsCounter(text: string): SmsCounterResult {
      if (!text) return { remaining: 0, segments: 0 };

      let isUnicode = false;
      let length = 0;

      for (const ch of text) {
        if (gsm7Set.has(ch)) {
          length++;
        } else if (gsm7ExtSet.has(ch)) {
          length += 2;
        } else {
          isUnicode = true;
          length = text.length; // UCS-2 every char counts as 1
          break;
        }
      }

      const perSegment = isUnicode ? 70 : 160;
      const perConcat = isUnicode ? 67 : 153;

      if (length <= perSegment) {
        return { remaining: perSegment - length, segments: 1 };
      } else {
        const segments = Math.ceil(length / perConcat);
        const charsInCurrent = length % perConcat || perConcat;
        return { remaining: perConcat - charsInCurrent, segments };
      }
    },

    // mutations
    showAddCustomerDialogModal() {
      this._showAddCustomer = true;
    },
    hideAddCustomerDialogModal() {
      this._showAddCustomer = false;
    },
    toggleAddShowNotesDialogModal() {
      this._showAddNotes = !this._showAddNotes;
    },
    setAddTaskModalVisibility(val: boolean) {
      this.showAddTasks = val;
    },
    toggleAddCustomerDialogModal() {
      this._showAddCustomer = !this._showAddCustomer;
    },
    showCallWarningModal() {
      this.showCallWarning = true;
    },
    hideCallWarning() {
      this.showCallWarning = false;
    },
    showIncomingCallModal() {
      this._showIncomingCallDialog = true;
    },
    hideIncomingCallModal() {
      this._showIncomingCallDialog = false;
    },
    setInCall(value: InCall) {
      this._inCall = value;
    },
    setCurrentCustomer(customer: Customer | null) {
      this._currentCustomer = customer;
    },
    showSmsPopUp() {
      this._isSmsPopUpVisible = true;
    },
    hideSmsPopUp() {
      this._isSmsPopUpVisible = false;
    },
    setCurrentCallHistoryId(id: number) {
      this._currentCallHistoryId = id;
    },
    setIsIncoming(value: boolean) {
      this._isIncoming = value;
    },
    // new methods for visibility
    setIncomingModalVisibility(value: boolean) {
      this._showIncomingCallDialog = value;
      if (value) {
        this.resetCallDuration();
        this.resetLastCallAgent();
        this.setOngoingModalVisibility(false);
        this.setMaximizeButtonVisibility(false);
      }
    },
    setOngoingModalVisibility(value: boolean) {
      this._showOngoingCallDialog = value;
    },
    setSmsPopUpVisibility(value: boolean) {
      this._isSmsPopUpVisible = value;
    },
    setBulkSmsPopUpVisibility(value: boolean) {
      this._isBulkSmsPopUpVisible = value;
    },
    setAddNoteModalVisibility(value: boolean) {
      this._showAddNotes = value;
    },
    setAddCustomerDialogVisibility(value: boolean) {
      this._showAddCustomer = value;
    },
    setTransferCallModalVisibility(value: boolean) {
      this._showTransferCallModal = value;
    },
    setSmsNumber(number: string) {
      this._smsNumber = number;
    },
    setDialerVisibility(val: boolean) {
      this.showDialer = val;
    },
    setShortcutListVisibility(val: boolean) {
      this._showShortcutList = val;
    },
    setShowMoreModal(val: boolean) {
      this._showMoreModal = val;
    },
    setOngoingCallOrigin(origin: CallOrigin) {
      this._ongoingCallOrigin = origin;
    },
    setTeleSaleEditDialogVisibility(val: boolean) {
      this._showTeleSalesEditDialog = val;
    },
    setDialerPhoneNo(val: string) {
      this._phoneNoToSet = val;
    },
    setOngoingCallNumber(val: string) {
      this._ongoingCallNumber = val;
    },
    setCurrentProfile(profile: Profile | null) {
      this._currentProfile = profile;
    },
    incrementCallDuration() {
      this._callDuration++;
    },
    resetCallDuration() {
      this._callDuration = 0;
    },
    setMaximizeButtonVisibility(val: boolean) {
      this._showMaximizeButton = val;
    },
    setTeleSaleInfo(info: object) {
      this._teleSaleInfo = info;
    },
    async fetchLastCallAgent(phone: string) {
      this._lastCallAgent = null;
      const without =
        "tasks,category,sub_category,sub_sub_category,closing_status,brand,profile";
      const { response, error } = await getRequest<any>(
        `cc/agent-call-histories?customer_mobile_no=${encodeURIComponent(
          phone,
        )}&sort_column=id&dir=desc&length=1&without=${without}`,
      );
      if (!error && response.data?.data?.length) {
        this._lastCallAgent = response.data.data[0].prev_agent ?? null;
      }
    },
    setLastCallAgent(agent: Pick<User, "id" | "name" | "user_name"> | null) {
      this._lastCallAgent = agent;
    },
    resetLastCallAgent() {
      this._lastCallAgent = null;
    },
  },
  getters: {
    inCall: (state) => state._inCall,
    currentCustomer: (state) => state._currentCustomer,
    transferableAgents: (state) => state._transferableAgents,
    isSmsPopUpVisible: (state) => state._isSmsPopUpVisible,
    showMoreModal: (state) => state._showMoreModal,
    isIncoming: (state) => state._isIncoming,

    showIncomingCallDialog: (state) => state._showIncomingCallDialog,
    showOngoingCallDialog: (state) => state._showOngoingCallDialog,
    showAddCustomer: (state) => state._showAddCustomer,
    showAddNotes: (state) => state._showAddNotes,
    ongoingCallOrigin: (state) => state._ongoingCallOrigin,
    smsNumber: (state) => state._smsNumber,
    currentCallHistoryId: (state) => state._currentCallHistoryId,
    showTransferCallModal: (state) => state._showTransferCallModal,
    showTeleSalesEditDialog: (state) => state._showTeleSalesEditDialog,
    phoneNoToSet: (state) => state._phoneNoToSet,
    ongoingCallNumber: (state) => state._ongoingCallNumber,
    currentProfile: (state) => state._currentProfile,
    callDuration: (state) => state._callDuration,
    teleSaleInfo: (state) => state._teleSaleInfo,
    lastCallAgent: (state) => state._lastCallAgent,
  },
});
